import * as React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import CloseIcon from "@mui/icons-material/Close";
import AuthPasswordField from "../Auth/AuthPasswordField";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { getAuthPrimaryButtonSx } from "../Auth/authGlassStyles";
import { shapeShell } from "../../theme/shape";
import {
  actionMenuMenuListSx,
  getActionMenuPaperSx,
  getDashboardAccountMenuItemDangerSx,
  getDashboardAccountMenuItemSx,
} from "../../utils/actionMenuStyles";
import ActionMenuDividerLine from "../GlobalStyles/ActionMenuDividerLine";
import { LoadingButton } from "@mui/lab";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import Cookies from "js-cookie";
import { useGlobalAuthContext } from "../../context/AuthContext";
import { useUserProfileSelector } from "../../hooks/useUserProfile";
import {
  getRoleFromAuthToken,
  isPartnerAdminJwtRole,
} from "../../utils/routeAccess";
import { apiRoomNate, useChangePswMutation } from "../../Slices/roomNateApis";
import { DynamicAlert } from "../../utils/Alerts";
import UserProfileEditDialog from "./UserProfileEditDialog";
import AccountEditDialog from "./AccountEditDialog";

/**
 * Top bar on `/main`: user email + account menu (aligned with MF logo row).
 */
export default function MainHomeHeaderActions() {
  const theme = useTheme();
  const navigate = useNavigate();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const { Logout, currentUserSessionStorage } = useGlobalAuthContext();
  const { userProfile: profile } = useUserProfileSelector();

  const showAnalyticsMenuItem = React.useMemo(() => {
    let authToken = currentUserSessionStorage;
    if (!authToken) {
      const cookieToken = Cookies.get("token");
      if (cookieToken) {
        try {
          authToken = JSON.parse(cookieToken);
        } catch {
          authToken = cookieToken;
        }
      }
    }
    return isPartnerAdminJwtRole(getRoleFromAuthToken(authToken));
  }, [currentUserSessionStorage]);

  const [pswDialogOpen, setPswDialogOpen] = React.useState(false);
  const [showOldPsw, setShowOldPsw] = React.useState(false);
  const [showNewPsw, setShowNewPsw] = React.useState(false);
  const [showConfirmPsw, setShowConfirmPsw] = React.useState(false);
  const [pswLoad, setPswLoad] = React.useState(false);
  const [pswTitle, setPswTitle] = React.useState("");
  const [pswStatus, setPswStatus] = React.useState(true);
  const [actionsMenuAnchor, setActionsMenuAnchor] = React.useState(null);
  const [accountDialogOpen, setAccountDialogOpen] = React.useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = React.useState(false);

  const [changePsw] = useChangePswMutation();

  const email = profile?.email?.trim() || "";

  const pswFormik = useFormik({
    initialValues: {
      password: "",
      oldPassword: "",
      confirmPassword: "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      oldPassword: Yup.string()
        .required("No old password provided.")
        .min(8, "Password is too short - should be 8 chars minimum."),
      password: Yup.string()
        .required("No password provided.")
        .min(8, "Password is too short - should be 8 chars minimum."),
      confirmPassword: Yup.string()
        .required("No confirm password provided.")
        .oneOf([Yup.ref("password"), null], "Passwords must match"),
    }),
    onSubmit: async (values, { resetForm }) => {
      setPswLoad(true);
      try {
        const res = await changePsw({
          body: {
            currentPassword: values?.oldPassword,
            newPassword: values?.password,
          },
        }).unwrap();
        setPswLoad(false);
        setPswTitle(res?.message || "Password updated.");
        setPswStatus(true);
        resetForm();
        setPswDialogOpen(false);
      } catch (err) {
        setPswLoad(false);
        const d = err?.data;
        const msg =
          (typeof d === "string" ? d : d?.error) ??
          err?.message ??
          "Could not update password.";
        setPswTitle(typeof msg === "string" ? msg : "Could not update password.");
        setPswStatus(false);
      }
    },
  });

  React.useEffect(() => {
    if (pswTitle) {
      const t = setTimeout(() => setPswTitle(""), 2500);
      return () => clearTimeout(t);
    }
  }, [pswTitle]);

  const handleLogout = () => {
    Logout(true);
    dispatch(apiRoomNate.util.resetApiState());
  };

  return (
    <>
      {pswTitle?.length >= 1 && (
        <DynamicAlert status={pswStatus} title={pswTitle} />
      )}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={{ xs: 0.75, sm: 1 }}
        sx={{ minWidth: 0, flexShrink: 0, ml: "auto", pl: 1 }}
      >
        {email ? (
          <Typography
            variant="body2"
            component="span"
            sx={{
              color: "text.secondary",
              fontSize: { xs: "0.6875rem", sm: "0.75rem" },
              lineHeight: 1.3,
              maxWidth: { xs: "min(48vw, 220px)", sm: 320 },
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: { xs: "block", md: "block" },
            }}
          >
            {email}
          </Typography>
        ) : null}

        <IconButton
          aria-label="Account actions"
          size="small"
          onClick={(e) => setActionsMenuAnchor(e.currentTarget)}
          sx={{
            flexShrink: 0,
            width: 36,
            height: 36,
            color: "#545454",
            bgcolor: "rgba(255,255,255,0.9)",
            border: "1px solid rgba(0,0,0,0.08)",
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            "&:hover": {
              bgcolor: "rgba(248,248,250,1)",
              borderColor: "rgba(0,0,0,0.12)",
            },
          }}
        >
          <MoreHorizIcon sx={{ fontSize: 22 }} />
        </IconButton>

        <Menu
          anchorEl={actionsMenuAnchor}
          open={Boolean(actionsMenuAnchor)}
          onClose={() => setActionsMenuAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              ...getActionMenuPaperSx(theme, "dropdown"),
              width: { xs: "min(92vw, 260px)", sm: 296 },
              minWidth: { xs: "min(92vw, 260px)", sm: 296 },
              maxWidth: { xs: "min(92vw, 260px)", sm: 296 },
              mt: 0.5,
            },
          }}
          MenuListProps={{ sx: actionMenuMenuListSx }}
        >
          <MenuItem
            onClick={() => {
              setActionsMenuAnchor(null);
              setAccountDialogOpen(true);
            }}
            sx={getDashboardAccountMenuItemSx(theme)}
          >
            My account
          </MenuItem>
          <MenuItem
            onClick={() => {
              setActionsMenuAnchor(null);
              pswFormik.resetForm();
              setPswDialogOpen(true);
            }}
            sx={getDashboardAccountMenuItemSx(theme)}
          >
            My password
          </MenuItem>
          <MenuItem
            onClick={() => {
              setActionsMenuAnchor(null);
              setProfileDialogOpen(true);
            }}
            sx={getDashboardAccountMenuItemSx(theme)}
          >
            My branding
          </MenuItem>
          {showAnalyticsMenuItem ? (
            <MenuItem
              onClick={() => {
                setActionsMenuAnchor(null);
                navigate("/analytics");
              }}
              sx={getDashboardAccountMenuItemSx(theme)}
            >
              Analytics
            </MenuItem>
          ) : null}
          <ActionMenuDividerLine />
          <MenuItem
            onClick={() => {
              setActionsMenuAnchor(null);
              handleLogout();
            }}
            sx={getDashboardAccountMenuItemDangerSx(theme)}
          >
            Log out
          </MenuItem>
        </Menu>
      </Stack>

      <Dialog
        open={pswDialogOpen}
        onClose={() => !pswLoad && setPswDialogOpen(false)}
        fullScreen={fullScreenDialog}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        sx={{
          "& .MuiDialog-paper": {
            ...(fullScreenDialog && { maxHeight: "100dvh" }),
            ...(!fullScreenDialog && { borderRadius: shapeShell }),
          },
          "& .MuiDialogContent-root": {
            WebkitOverflowScrolling: "touch",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            letterSpacing: "-0.02em",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: { xs: 2.5, sm: 3 },
          }}
        >
          Change your password
          <IconButton
            aria-label="Close"
            onClick={() => setPswDialogOpen(false)}
            disabled={pswLoad}
            size="small"
            sx={{ color: "text.secondary", ml: 1.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            px: { xs: 2, sm: 3 },
            pb: {
              xs: "max(env(safe-area-inset-bottom, 0px) + 24px, 24px)",
              sm: 3,
            },
            pt: 0,
          }}
        >
          <Stack gap={2} sx={{ mt: 1 }}>
            <AuthPasswordField
              required
              fullWidth
              label={
                pswFormik.touched.oldPassword && pswFormik.errors.oldPassword
                  ? pswFormik.errors.oldPassword
                  : "Current password"
              }
              name="oldPassword"
              value={pswFormik.values.oldPassword}
              onChange={pswFormik.handleChange}
              error={
                pswFormik.touched.oldPassword &&
                Boolean(pswFormik.errors.oldPassword)
              }
              onBlur={pswFormik.handleBlur}
              InputLabelProps={
                pswFormik.touched.oldPassword && pswFormik.errors.oldPassword
                  ? { shrink: true }
                  : undefined
              }
              autoComplete="current-password"
              showPsw={showOldPsw}
              onToggle={() => setShowOldPsw((v) => !v)}
            />

            <AuthPasswordField
              required
              fullWidth
              label={
                pswFormik.touched.password && pswFormik.errors.password
                  ? pswFormik.errors.password
                  : "New password"
              }
              name="password"
              value={pswFormik.values.password}
              onChange={pswFormik.handleChange}
              error={
                pswFormik.touched.password && Boolean(pswFormik.errors.password)
              }
              onBlur={pswFormik.handleBlur}
              InputLabelProps={
                pswFormik.touched.password && pswFormik.errors.password
                  ? { shrink: true }
                  : undefined
              }
              autoComplete="new-password"
              showPsw={showNewPsw}
              onToggle={() => setShowNewPsw((v) => !v)}
            />

            <AuthPasswordField
              required
              fullWidth
              label={
                pswFormik.touched.confirmPassword &&
                pswFormik.errors.confirmPassword
                  ? pswFormik.errors.confirmPassword
                  : "Confirm new password"
              }
              name="confirmPassword"
              value={pswFormik.values.confirmPassword}
              onChange={pswFormik.handleChange}
              error={
                pswFormik.touched.confirmPassword &&
                Boolean(pswFormik.errors.confirmPassword)
              }
              onBlur={pswFormik.handleBlur}
              InputLabelProps={
                pswFormik.touched.confirmPassword &&
                pswFormik.errors.confirmPassword
                  ? { shrink: true }
                  : undefined
              }
              autoComplete="new-password"
              showPsw={showConfirmPsw}
              onToggle={() => setShowConfirmPsw((v) => !v)}
            />

            {/* Action row: pill CTA, then centered reset link below. */}
            <Box
              sx={{
                mt: 0.5,
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                gap: "22px",
              }}
            >
              <LoadingButton
                loading={pswLoad}
                variant="contained"
                onClick={pswFormik.handleSubmit}
                sx={[getAuthPrimaryButtonSx(theme), { minHeight: 50 }]}
              >
                Change password
              </LoadingButton>

              <Button
                variant="text"
                disabled={pswLoad}
                onClick={() => {
                  pswFormik.resetForm();
                  setPswDialogOpen(false);
                  navigate("/forgetPsw");
                }}
                disableRipple
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  color: "text.secondary",
                  fontSize: "0.8125rem",
                  p: 0,
                  minHeight: "auto",
                  alignSelf: "center",
                  "&:hover": {
                    bgcolor: "transparent",
                    color: "text.primary",
                    textDecoration: "underline",
                  },
                }}
              >
                Forgot your password?
              </Button>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      <AccountEditDialog
        open={accountDialogOpen}
        onClose={() => setAccountDialogOpen(false)}
      />

      <UserProfileEditDialog
        open={profileDialogOpen}
        onClose={() => setProfileDialogOpen(false)}
      />
    </>
  );
}
